import { useState, useEffect } from 'react';
import { isOvertimeSuggestionOpen, overtimeSuggestionWindow } from '../../utils/navCounts';
import api from '../../hooks/useApi';
import { setPhoto } from '../../hooks/usePhotos';
import { formatDate } from '../../utils/format';
import { useApp } from '../../context';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { MY_CONTACT_TOUR_ID, myContactTourSteps } from './myContactTour';
import { MY_LEAVE_TOUR_ID, myLeaveTourSteps } from './myLeaveTour';
import { MY_BENEFITS_TOUR_ID, myBenefitsTourSteps } from './myBenefitsTour';
import { MY_ALLOWANCES_TOUR_ID, myAllowancesTourSteps } from './myAllowancesTour';
import { monthGrid } from '../../utils/standbyCalendar';
import { leaveDaysFor, UK_BANK_HOLIDAYS_FALLBACK, proposedChange, allowanceTotals, allowanceLeft, holidayYearOf } from '../../utils/leaveDays';
import { isEmployee } from '../../utils/employment';

// Bank holidays come from the server (migration 0272), which is also what the
// leave request check counts against: item 23's "usual bank holidays added,
// don't need booking" only works if the two agree, and they were two hardcoded
// lists in two files.
//
// The day count itself moved to utils/leaveDays on 21 September 2026, when a
// manager gained a second form for recording an absence for somebody in their
// team. Two forms with two copies of this arithmetic is what item 29 was about,
// so there is one copy and both import it.

// ── Benefit year helpers ──────────────────────────────────────────────────────
function currentBenefitYear() {
  const d = new Date(); const yr = d.getFullYear(); const mo = d.getMonth() + 1;
  return mo >= 3 ? `${yr}-${String(yr+1).slice(-2)}` : `${yr-1}-${String(yr).slice(-2)}`;
}
function benefitYearLabel(y) {
  if (!y) return '';
  const [a] = y.split('-');
  const b = parseInt(a) + 1;
  return `1 Mar ${a} – 28 Feb ${b}`;
}

// One name per kind of leave, matching what the server stores. These had drifted
// into three overlapping sets — the request form offered "Benefits Leave" while
// the allowance was written as tenure_annual, so a purchased day was checked
// against an allowance that did not exist under that name and was refused.
// Migration 0274 settled the names; this list follows it.
const LEAVE_TYPES = [
  { value: 'annual',           label: 'Annual Leave' },
  { value: 'carry_over',       label: 'Carried Over' },
  { value: 'tenure_annual',    label: 'Long Service' },
  { value: 'benefits_annual',  label: 'Benefits Leave' },
  { value: 'wellbeing',        label: 'Life Admin / Wellbeing Day' },
  { value: 'sick',             label: 'Sick Leave' },
  { value: 'company_training', label: 'Company Training' },
  { value: 'toil',             label: 'TOIL' },
  { value: 'other',            label: 'Other' },
];

// The allowances that get a box of their own on Leave & Holidays, because they
// behave differently from the rest: wellbeing days are granted rather than
// accrued and do not carry over, carried-over days expire in May, and TOIL is
// credited when a TOIL claim is approved in Approval Requests, capped per grade
// by the Overtime Policy, and does not carry over either. Benefits Leave used to
// have a box too (item 31), but a card under Total Entitlement repeating one of
// the numbers inside it read as a second allowance, and the per-type breakdown
// below already shows it, so it went.
const SEPARATE_BOX_TYPES = {
  toil: {
    label: 'TOIL',
    note: 'Time off in lieu of overtime, credited when a TOIL claim is approved. Bookable like annual leave, but does not carry over.',
    accent: 'text-sky-600',
  },
  wellbeing: {
    label: 'Wellbeing Day',
    note: 'Life admin or wellbeing. Must be taken as a standalone day, and does not carry over.',
    accent: 'text-teal-600',
  },
  carry_over: {
    label: 'Carried Over',
    note: null,   // filled in with the real deadline below
    accent: 'text-amber-600',
  },
};

const STATUS_COLORS = {
  pending:  'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled:'bg-gray-100 text-gray-500',
};

const OT_TYPE_LABELS  = {
  overtime:           'Overtime',
  standby:            'Standby',
  toil:               'TOIL',
  call_out:           'Call-Out',
  shift_move:         'Shift Move',
  working_away:       'Working Away',
  desk_move_weekday:  'Desk Move (Weekday)',
  desk_move_weekend:  'Desk Move (Weekend)',
  remote_working:     'Remote Working',
  out_of_hours:       'Out of Hours (>5PM / Sat)',
};

// Which types belong to the Allowances sub-tab
const ALLOWANCE_TYPES = ['desk_move_weekday', 'desk_move_weekend', 'remote_working', 'out_of_hours'];

// How the overtime ledger describes a suggestion (see src/lib/overtimeLedger.js).
// The label names the day the hours fell on, not what the policy pays for it:
// overtime carries no rate on any screen but the finance Overtime page.
const OT_SUGGESTION_LABELS = {
  bank_holiday:     'Bank holiday',
  sunday:           'Sunday',
  saturday:         'Saturday',
  weekday_over_cap: 'Over your daily cap',
  weekly_excess:    'Over your weekly hours',
};

// ── Helpers for month grouping ────────────────────────────────────────────────
function monthKey(dtStr) {
  if (!dtStr) return null;
  const s = dtStr.replace(' ', 'T');
  const d = new Date(s);
  if (isNaN(d)) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(key) {
  if (!key) return '';
  const [y, m] = key.split('-');
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
}
function calcHours(start, end) {
  if (!start || !end) return '';
  const diff = (new Date(end) - new Date(start)) / 3600000;
  return diff > 0 ? parseFloat(diff.toFixed(2)) : '';
}
// With the weekday, because a claim is checked against the days it fell on. A
// standby record raised from a PLOW carries a date and no time, so it shows as
// a day rather than as a made-up midnight.
function formatDatetime(dtStr) {
  if (!dtStr) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dtStr)) {
    return new Date(`${dtStr}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  const d = new Date(dtStr.replace(' ', 'T'));
  if (isNaN(d)) return dtStr;
  return d.toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}
// When a claim fell, as a string that sorts: 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:MM'.
function otWhen(r) {
  return (r.start_datetime || r.date || '').replace(' ', 'T');
}

// The holiday year rule is shared with the home dashboard, so it lives in
// utils/leaveDays.js; see holidayYearOf() there.
const fiscalYearFor = holidayYearOf;

function currentFiscalYear() {
  return fiscalYearFor(new Date().toISOString());
}

// The heading each page carries. It used to say "My Info" on all four tabs,
// which is no longer enough to say where you are now that they are four
// addresses in the sidebar.
const PAGE_TITLES = {
  contact: 'My Contact Details',
  leave: 'My Leave & Holidays',
  benefits: 'My Benefits',
  overtime: 'My Overtime',
};

// One guided tour per page, keyed the same way. A tour follows the page it
// opens on, never the component: Contact Details must not run the leave tour
// because the two happen to share an implementation.
const TOURS = {
  contact:  { id: MY_CONTACT_TOUR_ID,    steps: myContactTourSteps },
  leave:    { id: MY_LEAVE_TOUR_ID,      steps: myLeaveTourSteps },
  benefits: { id: MY_BENEFITS_TOUR_ID,   steps: myBenefitsTourSteps },
  overtime: { id: MY_ALLOWANCES_TOUR_ID, steps: myAllowancesTourSteps },
};

// What used to be the four tabs of My Info. The state behind them is one
// record — `me`, the leave allowances, the benefit selections and the overtime
// requests all come from the same load — so the four pages share this one
// implementation and differ only in which part of it they render.
export default function MyInfoPage({ tab }) {
  const { currentUser } = useApp();
  const { toast } = useToast();
  const [me, setMe] = useState(null);
  const [allowances, setAllowances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showRequest, setShowRequest]     = useState(false);
  const [submitError, setSubmitError]     = useState('');
  // The approved request the dialog is asking to change, or null when it is
  // filing a new one. The same form either way, so the day count is the same.
  const [changing, setChanging]           = useState(null);
  const [submitting, setSubmitting]       = useState(false);
  const [myBenefits, setMyBenefits]       = useState(null);
  const [allBenefits, setAllBenefits]     = useState([]);
  // The coming year's position, present only while selection for it is open
  // (February), so people can choose what starts on 1 March.
  const [nextYearBenefits, setNextYearBenefits] = useState(null);
  // The year the selection dialog is open for, or null when it is closed.
  const [benSelectYear, setBenSelectYear] = useState(null);
  // Standard benefits: outside the points scheme, each with where this person
  // stands on it (qualifies now, the date they will, or excluded).
  const [standardBenefits, setStandardBenefits] = useState([]);
  const benefitYear = currentBenefitYear();
  const [otRequests, setOtRequests]       = useState([]);
  const [showOtModal, setShowOtModal]     = useState(false);
  const [otModalType, setOtModalType]     = useState('overtime'); // pre-sets the type when opening log modal
  const [selectedOt, setSelectedOt]       = useState(null);
  const [otSubTab, setOtSubTab]           = useState('overtime'); // overtime | standby | call_out | allowances
  // For the allowances sub-tab, track which specific type the modal should log
  const [allowanceModalType, setAllowanceModalType] = useState('desk_move_weekday');
  const [otMonth, setOtMonth]             = useState(null); // selected month key e.g. "2026-03"
  const [otView, setOtView]               = useState('list'); // list | calendar
  const today = new Date().toISOString().split('T')[0];
  const nowDt = new Date(); nowDt.setSeconds(0, 0);
  const todayDtStr = nowDt.toISOString().slice(0, 16);
  const [otForm, setOtForm] = useState({ request_type: 'overtime', start_datetime: todayDtStr, end_datetime: '', reason: '' });
  const setOt = (k, v) => setOtForm(p => {
    const next = { ...p, [k]: v };
    // A form opened from a suggestion keeps the date and the two times as
    // separate fields; the start and end datetimes follow from them.
    if (k === 'date' || k === 'start_time' || k === 'end_time') {
      next.start_datetime = next.date && next.start_time ? `${next.date}T${next.start_time}` : '';
      next.end_datetime = next.date && next.end_time ? `${next.date}T${next.end_time}` : '';
    }
    return next;
  });
  const [form, setForm] = useState({
    personal_email: '', work_phone: '', personal_mobile: '', preferred_name: '',
    address_line1: '', address_line2: '', city: '', county: '', postcode: '',
    emergency_contact_name: '', emergency_contact_phone: '',
  });
  const [reqForm, setReqForm] = useState({
    leave_type: 'annual', start_date: '', end_date: '', days: '', reason: '',
    // Half-day markers: a request can start at lunchtime (start_half 'pm') or
    // end at it (end_half 'am'); on a one-day request either makes it that half.
    start_half: 'full', end_half: 'full',
  });

  const fiscalYear = currentFiscalYear();

  // Held back until the record exists: the page shows Loading… until then,
  // with nothing for a step to point at.
  const tour = useFeatureTour(TOURS[tab].id, { enabled: !!me });

  // Suggestions from the time log: what the overtime ledger infers for this
  // person over the last two months that nobody has claimed or set aside. A
  // claim starts from one of these rather than from a blank form. What was set
  // aside stays listed, with an undo, for a week and then drops off.
  const [otSuggestions, setOtSuggestions] = useState([]);
  const [otSuggestBusy, setOtSuggestBusy] = useState(null);   // key being acted on
  const [otSuggestNote, setOtSuggestNote] = useState({});     // key -> message shown beside the row
  const loadSuggestions = () => {
    // The window the sidebar's My Overtime badge counts over too.
    api.get('overtime-ledger/mine', overtimeSuggestionWindow())
      .then(d => setOtSuggestions((d?.rows || []).filter(r => r.status === 'inferred' || r.status === 'dismissed')))
      .catch(() => setOtSuggestions([]));
  };
  const loadOt = () => {
    api.get('overtime-requests').then(data => {
      const all = Array.isArray(data) ? data : [];
      setOtRequests(all.filter(r => r.person_id === currentUser?.id));
    }).catch(() => {});
    loadSuggestions();
  };
  // A suggestion opens the request form with what the time log knows filled
  // in, rather than raising the claim there and then. The log has the day and
  // the hours but not when the overtime started or finished, and those are
  // what the approver and finance need, so the person supplies them. Only what
  // the log knows is filled in: the date, the hours as a hint, and the reason.
  // No clock time is invented, because a plausible default would be submitted
  // unchanged and then read as fact. The date and the two times are separate
  // fields here and are composed into start and end datetimes as they are set.
  // The key travels with the form so the submit still confirms this ledger row.
  const claimSuggestion = (row) => {
    const type = row.eligibility === 'toil' ? 'toil' : 'overtime';
    const day = new Date(`${row.date}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    const reason = `${OT_SUGGESTION_LABELS[row.kind] || row.kind}, ${row.kind === 'weekly_excess' ? 'week of ' : ''}${day}: ${row.hours}h`
      + (row.sow_references?.length ? ` (${row.sow_references.join(', ')})` : '')
      + (row.description ? `. ${row.description}` : '');
    setOtModalType(type);
    setOtForm({
      request_type: type,
      date: row.date,
      start_time: '',
      end_time: '',
      start_datetime: '',
      end_datetime: '',
      reason,
      suggestion_key: row.key,
      suggestion: { date: row.date, hours: row.hours, kind: row.kind, sow_references: row.sow_references || [] },
    });
    setShowOtModal(true);
  };
  const dismissSuggestion = async (row) => {
    setOtSuggestBusy(row.key);
    try {
      await api.post('overtime-ledger/dismiss', { key: row.key });
      loadSuggestions();
    } catch (e) {
      setOtSuggestNote(n => ({ ...n, [row.key]: e.message || 'Could not set this aside.' }));
    } finally { setOtSuggestBusy(null); }
  };
  const restoreSuggestion = async (row) => {
    setOtSuggestBusy(row.key);
    try {
      await api.delete(`overtime-ledger/dismiss/${encodeURIComponent(row.key)}`);
      loadSuggestions();
    } catch (e) {
      setOtSuggestNote(n => ({ ...n, [row.key]: e.message || 'Could not restore this.' }));
    } finally { setOtSuggestBusy(null); }
  };

  const submitOtRequest = async () => {
    if (!otForm.start_datetime || !otForm.end_datetime) return;
    const hours = calcHours(otForm.start_datetime, otForm.end_datetime);
    if (!hours || hours <= 0) { toast.error('End time must be after start time.'); return; }
    // Derive date from start_datetime for compatibility
    const date = otForm.start_datetime.slice(0, 10);
    try {
      if (otForm.suggestion_key) {
        // Started from a time-log suggestion: claim that ledger row, with the
        // times and reason the person filled in, so the row is the one confirmed.
        const { suggestion_key, start_datetime, end_datetime, reason } = otForm;
        const res = await api.post('overtime-ledger/claim', { key: suggestion_key, start_datetime, end_datetime, reason });
        toast.success(`Sent to your manager for approval as ${res.request_type === 'toil' ? 'TOIL' : 'overtime'}.`);
      } else {
        await api.post('overtime-requests', { ...otForm, hours, date });
      }
    } catch (e) {
      toast.error(e.message || 'Could not raise the claim.');
      return;
    }
    setShowOtModal(false);
    setOtForm({ request_type: otModalType, start_datetime: todayDtStr, end_datetime: '', reason: '' });
    loadOt();
  };

  const load = async () => {
    try {
    const [p, a, lr, mb, ab, sb] = await Promise.all([
      api.get('people/me'),
      api.get(`people/${currentUser?.id}/leave-allowances`, { year: fiscalYear }).catch(() => []),
      api.get(`people/${currentUser?.id}/leave-requests`).catch(() => []),
      api.get('my-benefits').catch(() => null),
      api.get('benefits').catch(() => []),
      api.get('my-standard-benefits').catch(() => []),
    ]);
    setMe(p);
    setAllowances(Array.isArray(a) ? a : []);
    setRequests(Array.isArray(lr) ? lr : []);
    setMyBenefits(mb);
    const nextOpenYear = mb?.enrolment_years?.find(y => y !== mb.year);
    setNextYearBenefits(nextOpenYear ? await api.get('my-benefits', { year: nextOpenYear }).catch(() => null) : null);
    setAllBenefits(Array.isArray(ab) ? ab.filter(b => b.is_active && b.is_selectable) : []);
    setStandardBenefits(Array.isArray(sb) ? sb : []);
    setForm({
      personal_email:          p.personal_email || '',
      work_phone:               p.work_phone || '',
      personal_mobile:          p.personal_mobile || '',
      preferred_name:           p.preferred_name || '',
      // The structured address, which is what the People record shows. It used to write
      // the single home_address column, so a house move saved and was flagged to HR but
      // never appeared on the person's record.
      address_line1:            p.address_line1 || '',
      address_line2:            p.address_line2 || '',
      city:                     p.city || '',
      county:                   p.county || '',
      postcode:                 p.postcode || '',
      emergency_contact_name:   p.emergency_contact_name || '',
      emergency_contact_phone:  p.emergency_contact_phone || '',
    });
    } catch (e) { console.error('MyInfoPage load error:', e); }
  };

  useEffect(() => { if (currentUser?.id) { load(); loadOt(); } }, [currentUser]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setR = (k, v) => setReqForm(p => ({ ...p, [k]: v }));

  const saveContact = async () => {
    setSaving(true);
    try {
      const updated = await api.patch('people/me', form);
      setMe(updated);
      // Item 21: the save goes through and HR is told, rather than the change
      // waiting on an approval. Which of the two happened is said out loud —
      // "Saved" alone would leave somebody wondering whether HR knows, and the
      // whole point of telling them is that the person does not have to.
      setSaved(updated?.flagged_to_hr?.length ? 'flagged' : true);
      setTimeout(() => setSaved(false), 6000);
    } finally {
      setSaving(false);
    }
  };

  const submitBenefitRequest = async (selectedIds) => {
    await api.post('benefit-change-requests', {
      benefit_year: benSelectYear || benefitYear,
      requested_selections: selectedIds,
    });
    setBenSelectYear(null);
    load();
  };

  // The real bank holidays, from the same table the server counts against. Loaded
  // once; the hardcoded fallback covers the moment before it arrives.
  const [bankHolidays, setBankHolidays] = useState(UK_BANK_HOLIDAYS_FALLBACK);
  // The same rows, kept as rows. The feed carries company closures alongside the bank
  // holidays (both are days nobody books), and item 23 asked for them to be listed out.
  const [daysOffRows, setDaysOffRows] = useState([]);
  useEffect(() => {
    api.get('bank-holidays')
      .then(rows => {
        if (Array.isArray(rows) && rows.length) {
          setBankHolidays(new Set(rows.map(r => r.holiday_date)));
          setDaysOffRows(rows);
        }
      })
      .catch(() => {});
  }, []);

  // Only the ones still to come: a list that opens with last January is a list nobody
  // reads.
  // Part-time changes what a bank holiday IS here: their allowance is worked out
  // to include them, so they are booked against it rather than being free on top.
  const isPartTime = (me?.fte ?? 1) < 1;

  const upcomingDaysOff = (() => {
    const today = new Date().toISOString().slice(0, 10);
    return daysOffRows
      .filter(h => h?.holiday_date && h.holiday_date >= today)
      .sort((a, b) => a.holiday_date.localeCompare(b.holiday_date))
      .slice(0, 16);
  })();

  // Auto-calculate working days when dates or half-day markers change, with the
  // same function the manager's Record an absence form uses. This is the only
  // thing that writes the figure: the box reports it and cannot be typed into,
  // because the server recalculates it the same way and a typed-over number was
  // only ever going to disagree with the allowance it was charged against.
  //
  // A part-timer whose working days are on their record is charged exactly
  // those days; without them, their FTE average (see utils/leaveDays). A range
  // with nothing bookable in it, or an end before its start, clears the figure:
  // left stale, a box nobody can correct would submit a number belonging to
  // dates that are no longer on screen.
  useEffect(() => {
    if (!reqForm.start_date || !reqForm.end_date) return;
    const days = leaveDaysFor({
      start: reqForm.start_date, end: reqForm.end_date, fte: me?.fte || 1,
      workingDays: me?.working_days, bankHolidays,
      startHalf: reqForm.start_half, endHalf: reqForm.end_half,
    });
    if (days === '') {
      setReqForm(p => (p.days === '' ? p : { ...p, days: '' }));
      return;
    }
    setReqForm(p => ({ ...p, days: String(days) }));
  }, [reqForm.start_date, reqForm.end_date, reqForm.start_half, reqForm.end_half, bankHolidays, me?.fte, me?.working_days]);

  // ── WITHDRAWING LEAVE YOU NO LONGER NEED ───────────────────────────────────
  //
  // Cancel used to be offered on a pending request only, so somebody whose
  // plans changed after their holiday was approved had nowhere to say so and
  // had to ask HR. Approved leave is yours to give back right up until the day
  // it starts; from the first morning of it, it is a record of time taken and
  // only HR should be rewriting that. The server holds the same rule, so this
  // is not the check, it is the reason the button is or is not there.
  const canCancelLeave = (r) =>
    (r.status === 'pending' || r.status === 'approved')
    && (r.start_date || '').slice(0, 10) > new Date().toISOString().slice(0, 10);

  const cancelLeaveRequest = async (r) => {
    if (!window.confirm(r.status === 'approved'
      ? `Cancel this approved leave? Its ${r.days} day${r.days === 1 ? '' : 's'} go back to your allowance.`
      : 'Cancel this leave request?')) return;
    try {
      await api.put(`leave-requests/${r.id}`, { status: 'cancelled' });
      load();
    } catch (e) { toast.error('Failed to cancel: ' + e.message); }
  };

  // ── CHANGING APPROVED LEAVE ─────────────────────────────────────────────────
  //
  // Moving approved leave used to mean cancelling it and asking again, with the
  // approved week gone while the new one waited. Now the change goes back to
  // the approver and the approved leave stays booked until they decide; if they
  // say no, nothing is lost. The window is the one cancelling has.
  const canChangeLeave = (r) => r.status === 'approved' && canCancelLeave(r);

  const openChange = (r) => {
    setReqForm({
      leave_type: r.leave_type || 'annual',
      start_date: (r.start_date || '').slice(0, 10),
      end_date:   (r.end_date || '').slice(0, 10),
      days:       String(r.days ?? ''),
      reason:     r.reason || '',
      start_half: r.start_half || 'full',
      end_half:   r.end_half || 'full',
    });
    setSubmitError('');
    setChanging(r);
    setShowRequest(true);
  };

  const closeRequest = () => {
    setShowRequest(false);
    setSubmitError('');
    if (changing) {
      setChanging(null);
      setReqForm({ leave_type: 'annual', start_date: '', end_date: '', days: '', reason: '', start_half: 'full', end_half: 'full' });
    }
  };

  const withdrawChange = async (r) => {
    try {
      await api.delete(`leave-requests/${r.id}/change`);
      load();
    } catch (e) { toast.error('Failed to withdraw the change: ' + e.message); }
  };

  const submitLeaveRequest = async () => {
    if (!reqForm.start_date || !reqForm.end_date || !reqForm.days) return;
    if (reqForm.end_date < reqForm.start_date) {
      toast.error('End date must be on or after start date');
      return;
    }
    setSubmitting(true); setSubmitError('');
    try {
      const payload = {
        leave_type:  reqForm.leave_type,
        start_date:  reqForm.start_date,
        end_date:    reqForm.end_date,
        days:        parseFloat(reqForm.days),
        reason:      reqForm.reason || null,
        start_half:  reqForm.start_half || 'full',
        end_half:    reqForm.end_half || 'full',
      };
      if (changing) {
        await api.put(`leave-requests/${changing.id}/change`, payload);
        toast.success('Change sent for approval. Your leave stays as it is until your manager decides.');
      } else {
        await api.post('leave-requests', payload);
      }
      setShowRequest(false);
      setChanging(null);
      setReqForm({ leave_type: 'annual', start_date: '', end_date: '', days: '', reason: '', start_half: 'full', end_half: 'full' });
      load();
    } catch (e) {
      setSubmitError(e.message || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelOtRequest = async (id) => {
    if (!confirm('Cancel this request?')) return;
    await api.put(`overtime-requests/${id}`, { status: 'cancelled' });
    loadOt();
  };

  // The benefits scheme covers employees, permanent and fixed-term alike; only a
  // contractor is outside it. Until the record loads, assume the scheme applies.
  const inBenefitsScheme = !me || isEmployee(me.employment_type);

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      // Resize using canvas before saving to keep payload small
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const MAX = 256;
        const scale = Math.min(MAX / img.width, MAX / img.height, 1);
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
        // The server checks the photo is a small JPEG, PNG or WebP, and says why
        // when it is not.
        try {
          const updated = await api.patch('people/me', { profile_photo: dataUrl });
          setMe(updated);
          setPhoto(updated.id, updated.profile_photo);
        } catch (err) {
          toast.error(err.message || 'The photo could not be saved.');
        }
      };
      img.onerror = () => toast.error('That file could not be read as a picture. Choose a JPEG, PNG or WebP image.');
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  if (!me) return <div className="text-[10px] text-gray-400 py-8 text-center">Loading…</div>;

  // Allowance summary for current year
  const annualAllowance = allowances.find(a => a.leave_type === 'annual');
  // TOIL is offered on the request form only to somebody with some left to take;
  // there is nothing HR can set to give it, so an option nobody can use is noise.
  const toilAvailable = allowances.some(a => a.leave_type === 'toil' && ((a.total_days || 0) - (a.booked_days || 0)) > 0);
  const { entitlement: totalEntitlement, booked: totalBooked, remaining } = allowanceTotals(allowances);

  // Item 32: wellbeing days, carried-over days and TOIL get their own box. They
  // were folded into one "Total Entitlement" figure, which is exactly what El
  // asked to separate: a granted day and an accrued day are the same to that
  // total and are not the same to the person deciding what to book, especially
  // when they do not carry over or expire in May.
  const separateBoxes = Object.entries(SEPARATE_BOX_TYPES)
    .map(([type, meta]) => {
      const a = allowances.find(x => x.leave_type === type);
      if (!a || !(a.total_days > 0)) return null;
      return {
        type, ...meta,
        total: a.total_days || 0,
        booked: a.booked_days || 0,
        left: (a.total_days || 0) - (a.booked_days || 0),
        note: type === 'carry_over' && a.expires_on
          ? `Carried from last year. Must be taken by ${formatDate(a.expires_on)} or lost.`
          : meta.note,
        expiring: type === 'carry_over' && a.expires_on && (a.total_days - a.booked_days) > 0,
      };
    })
    .filter(Boolean);

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          {/* The circled ? is the same replay control as Bookings and My Time,
              down to the size and the label, so help reads as one convention. */}
          <div className="flex items-center gap-2">
            <h2 className="text-base font-extrabold text-navy">{PAGE_TITLES[tab]}</h2>
            <button
              type="button"
              onClick={tour.start}
              aria-label="Replay the guided tour of this page"
              title="Replay the guided tour of this page"
              className="w-[23px] h-[23px] shrink-0 rounded-full border border-gray-300 bg-white
                text-gray-500 text-[12px] font-semibold leading-none flex items-center justify-center
                transition-colors hover:border-navy hover:text-navy hover:bg-gray-50"
            >
              ?
            </button>
          </div>
          <div className="text-[9px] text-gray-500">{me.first_name} {me.last_name} · {me.designation || me.user_role}</div>
        </div>
        {/* Avatar / Profile photo — click to upload */}
        <label htmlFor="profile-photo-input" style={{ cursor: 'pointer', position: 'relative', display: 'inline-block' }} title="Click to upload profile photo">
          {me.profile_photo ? (
            <img src={me.profile_photo} alt="Profile" className="w-9 h-9 rounded-full object-cover" style={{ border: '2px solid #E5B87A' }} />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
              style={{ background: me.avatar_color || '#A1723D' }}>
              {(me.first_name?.[0] || '') + (me.last_name?.[0] || '')}
            </div>
          )}
          <div style={{ position: 'absolute', bottom: 0, right: 0, width: 14, height: 14, background: '#0D1D32', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #fff' }}>
            <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M5 1v8M1 5h8" stroke="#E5B87A" strokeWidth="2" strokeLinecap="round"/></svg>
          </div>
        </label>
        <input id="profile-photo-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
      </div>

      {/* ── CONTACT TAB ── */}
      {tab === 'contact' && (
        <div className="bg-white border border-gray-200 rounded p-4 max-w-xl">
          <div data-tour="contact-hr-fields">
          <div className="text-[8px] text-gray-400 uppercase font-bold mb-3">
            Read-only fields (managed by HR)
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div>
              <div className="text-[8px] text-gray-400 uppercase font-bold">Full Name</div>
              <div className="text-[10px] font-semibold">{me.first_name} {me.last_name}</div>
            </div>
            <div>
              <div className="text-[8px] text-gray-400 uppercase font-bold">Work Email</div>
              <div className="text-[10px]">{me.email_work}</div>
            </div>
            <div>
              <div className="text-[8px] text-gray-400 uppercase font-bold">Role</div>
              <div className="text-[10px] capitalize">{me.designation || me.user_role}</div>
            </div>
            <div>
              <div className="text-[8px] text-gray-400 uppercase font-bold">Team</div>
              <div className="text-[10px]">{me.team_name || '—'}</div>
            </div>
          </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-tour="contact-your-details">
            <div className="text-[8px] text-gray-400 uppercase font-bold mb-1">
              Your details — update anytime
            </div>
            <div className="text-[8px] text-gray-500 mb-3">
              Saved straight away, and HR is told what changed so their file matches. No approval to wait for.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Preferred Name</label>
                <input className="form-input" value={form.preferred_name} onChange={e => set('preferred_name', e.target.value)} placeholder="e.g. Alex" />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Personal Email</label>
                <input className="form-input" type="email" value={form.personal_email} onChange={e => set('personal_email', e.target.value)} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Work Phone</label>
                <input className="form-input" type="tel" value={form.work_phone} onChange={e => set('work_phone', e.target.value)} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Personal Mobile</label>
                <input className="form-input" type="tel" value={form.personal_mobile} onChange={e => set('personal_mobile', e.target.value)} />
              </div>
              {/* The address in its parts, which is how the People record holds it and
                  therefore how it reaches HR. One free-text box wrote a different column
                  that nothing displayed. */}
              <div className="col-span-2">
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address Line 1</label>
                <input className="form-input" value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="House number and street" />
              </div>
              <div className="col-span-2">
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address Line 2</label>
                <input className="form-input" value={form.address_line2} onChange={e => set('address_line2', e.target.value)} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Town or City</label>
                <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">County</label>
                <input className="form-input" value={form.county} onChange={e => set('county', e.target.value)} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Postcode</label>
                <input className="form-input" value={form.postcode} onChange={e => set('postcode', e.target.value)} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Emergency Contact Name</label>
                <input className="form-input" value={form.emergency_contact_name} onChange={e => set('emergency_contact_name', e.target.value)} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Emergency Contact Phone</label>
                <input className="form-input" type="tel" value={form.emergency_contact_phone} onChange={e => set('emergency_contact_phone', e.target.value)} />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4" data-tour="contact-save">
              <button
                onClick={saveContact}
                disabled={saving}
                className="px-4 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              {saved && (
                <span className="text-[9px] text-emerald-600 font-semibold">
                  {saved === 'flagged' ? '✓ Saved, and HR has been notified of the change' : '✓ Saved'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── LEAVE TAB ── */}
      {tab === 'leave' && (
        <div className="space-y-3">
          {/* Allowance summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-tour="leave-summary">
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Total Entitlement</div>
              <div className="text-lg font-extrabold text-navy">{totalEntitlement}d</div>
              <div className="text-[8px] text-gray-400">{fiscalYear}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Booked / Approved</div>
              <div className="text-lg font-extrabold text-amber-500">{totalBooked}d</div>
            </div>
            <div className={`bg-white border rounded p-2 ${remaining < 0 ? 'border-red-300' : 'border-gray-200'}`}>
              <div className={`text-[8px] uppercase font-bold ${remaining < 0 ? 'text-red-500' : 'text-gray-500'}`}>Remaining</div>
              <div className={`text-lg font-extrabold ${remaining < 0 ? 'text-red-500' : 'text-emerald-500'}`}>{remaining}d</div>
            </div>
          </div>

          {/* The allowances that behave differently, each in its own box rather
              than folded into the total above. */}
          {separateBoxes.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-tour="leave-extra-allowances">
              {separateBoxes.map(b => (
                <div key={b.type} className={`bg-white border rounded p-2 ${b.expiring ? 'border-amber-300' : 'border-gray-200'}`}>
                  <div className="text-[8px] text-gray-500 uppercase font-bold">{b.label}</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className={`text-lg font-extrabold ${b.accent}`}>{b.left}d</span>
                    <span className="text-[8px] text-gray-400">left of {b.total}d</span>
                  </div>
                  {b.note && <div className={`text-[8px] mt-0.5 ${b.expiring ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}>{b.note}</div>}
                </div>
              ))}
            </div>
          )}

          {/* ── THE DAYS YOU ALREADY HAVE OFF (item 23) ──
              "Can the bank holidays be listed out in My Info - Leave & Holidays so people
              can see these." Company closures are listed with them, because from a
              person's point of view they are the same thing: a day off that needs no
              booking and costs no allowance. Only the ones still to come — a list that
              opens with last January is a list nobody reads. */}
          {upcomingDaysOff.length > 0 && (
            <div className="bg-white border border-gray-200 rounded p-2" data-tour="leave-days-off">
              <div className="text-[9px] font-bold text-gray-600 mb-1">
                {isPartTime ? 'Days off in the diary' : 'Days off you do not need to book'}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {upcomingDaysOff.map(h => (
                  <span key={`${h.kind}-${h.holiday_date}`}
                    className={`text-[9px] rounded px-1.5 py-0.5 border ${h.kind === 'company_holiday'
                      ? 'bg-gold/10 border-gold/30 text-gold' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                    <strong>{formatDate(h.holiday_date)}</strong> {h.name}
                    {h.kind === 'company_holiday' ? ' · company' : ''}
                  </span>
                ))}
              </div>
              <div className="text-[8px] text-gray-400 mt-1">
                {isPartTime
                  ? 'Company closures come off nobody’s allowance. Your allowance is worked out to include the '
                    + 'bank holidays, so those are booked against it for you at the start of each holiday year — '
                    + 'you do not need to book them yourself'
                    + (me?.working_days ? ', and only the ones on the days you work count.' : '.')
                  : 'These come off nobody’s allowance. A leave request that spans one does not count it as a day taken.'}
              </div>
            </div>
          )}

          {/* Per-type allowance breakdown */}
          {allowances.length > 0 && (
            <div className="bg-white border border-gray-200 rounded overflow-x-auto" data-tour="leave-breakdown">
              <table className="w-full">
                <thead><tr>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Leave Type</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Allowance</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Booked</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Remaining</th>
                </tr></thead>
                <tbody>
                  {allowances.map(a => {
                    const rem = allowanceLeft(a);
                    const typeLabel = LEAVE_TYPES.find(t => t.value === a.leave_type)?.label || a.leave_type;
                    return (
                      <tr key={a.id} className="border-b border-gray-100">
                        <td className="px-2 py-1 text-[10px] font-semibold">{typeLabel}</td>
                        <td className="px-2 py-1 text-[10px] text-right">{rem === null ? '—' : `${a.total_days}d`}</td>
                        <td className="px-2 py-1 text-[10px] text-right text-amber-600">{a.booked_days || 0}d</td>
                        <td className={`px-2 py-1 text-[10px] text-right font-semibold ${rem === null ? 'text-gray-400' : rem < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{rem === null ? '—' : `${rem}d`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {allowances.length === 0 && (
            <div className="text-[10px] text-gray-400 py-3">No leave allowances set for {fiscalYear}. Please contact HR.</div>
          )}

          {/* Request leave button */}
          <div className="flex items-center justify-between">
            <div className="text-[9px] font-bold text-gray-600">Leave Requests — {fiscalYear}</div>
            <button onClick={() => setShowRequest(true)} data-tour="leave-request" className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80">
              + Request Leave
            </button>
          </div>

          {/* Leave requests list */}
          <div className="bg-white border border-gray-200 rounded overflow-x-auto" data-tour="leave-requests">
            <table className="w-full">
              <thead><tr>
                <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Type</th>
                <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">From</th>
                <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">To</th>
                <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Days</th>
                <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Status</th>
                <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Notes</th>
                <th className="px-2 py-1 border-b bg-gray-50" />
              </tr></thead>
              <tbody>
                {requests.filter(r => fiscalYearFor(r.start_date) === fiscalYear).map(r => {
                  const typeLabel = LEAVE_TYPES.find(t => t.value === r.leave_type)?.label || r.leave_type;
                  const change = proposedChange(r);
                  return (
                    <tr key={r.id} className="border-b border-gray-100 hover:bg-gold/5">
                      <td className="px-2 py-1 text-[10px] font-semibold">{typeLabel}</td>
                      <td className="px-2 py-1 text-[10px]">{formatDate(r.start_date)}{r.start_half === 'pm' ? ' (pm)' : ''}</td>
                      <td className="px-2 py-1 text-[10px]">{formatDate(r.end_date)}{r.end_half === 'am' ? ' (am)' : ''}</td>
                      <td className="px-2 py-1 text-[10px] text-right font-semibold">{r.days}d</td>
                      <td className="px-2 py-1">
                        <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded uppercase ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-500'}`}>
                          {r.status}
                        </span>
                        {change && (
                          <span className="ml-1 text-[7px] font-bold px-1.5 py-0.5 rounded uppercase bg-amber-100 text-amber-700">Change pending</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-[9px] text-gray-500">
                        {change ? (
                          <>Asked to change to {formatDate(change.start_date)}{change.start_half === 'pm' ? ' (pm)' : ''} – {formatDate(change.end_date)}{change.end_half === 'am' ? ' (am)' : ''}, {change.days}d{change.leave_type !== r.leave_type ? `, ${LEAVE_TYPES.find(t => t.value === change.leave_type)?.label || change.leave_type}` : ''}</>
                        ) : (r.rejection_reason || r.reason || '')}
                      </td>
                      <td className="px-2 py-1 text-right whitespace-nowrap">
                        {change ? (
                          <button onClick={() => withdrawChange(r)} className="text-[8px] text-navy hover:text-gold font-medium">Withdraw change</button>
                        ) : canChangeLeave(r) && (
                          <button onClick={() => openChange(r)} className="text-[8px] text-navy hover:text-gold font-semibold">Change</button>
                        )}
                        {canCancelLeave(r) && (
                          <button onClick={() => cancelLeaveRequest(r)} className="ml-2 text-[8px] text-red-400 hover:text-red-600 font-medium">Cancel</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {requests.filter(r => fiscalYearFor(r.start_date) === fiscalYear).length === 0 && (
                  <tr><td colSpan="6" className="text-center py-4 text-[10px] text-gray-400">No leave requests for {fiscalYear}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── BENEFITS TAB ── */}
      {tab === 'benefits' && !inBenefitsScheme && (
        <div className="bg-white border border-gray-200 rounded p-4 max-w-xl text-[10px] text-gray-500">
          The benefits scheme is for employees, so there is nothing here for a contractor.
        </div>
      )}

      {tab === 'benefits' && inBenefitsScheme && (
        <div className="space-y-3">
          {/* Two kinds of benefit, kept apart: flexible ones are chosen once a
              year with points; standard ones are everyone's who meets the
              criteria, with nothing to choose (the section below). */}
          <div className="border-b border-gold/40 pb-1">
            <div className="text-[11px] font-extrabold text-navy">Flexible Benefits</div>
            <div className="text-[8px] text-gray-500">Chosen once a year and paid for with points.</div>
          </div>

          {/* Year + points header */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-tour="benefits-points">
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Benefit Year</div>
              <div className="text-[11px] font-extrabold text-navy">{benefitYear}</div>
              <div className="text-[8px] text-gray-400">{benefitYearLabel(myBenefits?.year || benefitYear)}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Points Allocated</div>
              <div className="text-lg font-extrabold text-navy">{myBenefits?.points_allocated ?? '—'}</div>
              {myBenefits?.points_override
                ? <div className="text-[7px] text-blue-600">Set by HR</div>
                : myBenefits?.points_allocated < 4 && myBenefits?.points_allocated != null && (
                  <div className="text-[7px] text-amber-600">Pro-rated (new joiner)</div>
                )}
            </div>
            <div className={`bg-white border rounded p-2 ${(myBenefits?.points_remaining ?? 0) < 0 ? 'border-red-300' : 'border-gray-200'}`}>
              <div className="text-[8px] text-gray-500 uppercase font-bold">Points Remaining</div>
              <div className={`text-lg font-extrabold ${(myBenefits?.points_remaining ?? 0) < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {myBenefits?.points_remaining ?? '—'}
              </div>
              <div className="text-[7px] text-gray-400">{myBenefits?.points_used ?? 0} used</div>
            </div>
          </div>

          {/* Points come from the quarter you joined in (4, 3, 2, 1) unless HR has set them. */}
          {myBenefits?.points_override?.reason && (
            <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-[9px] text-blue-700">
              Your points for {benefitYear} were set by HR: {myBenefits.points_override.reason}
            </div>
          )}

          {/* Pending request banner */}
          {myBenefits?.pending_change_request && (
            <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-[9px] text-blue-700">
              <div className="font-semibold">✓ Change request submitted — awaiting HR review</div>
              <div className="text-[8px] mt-0.5 text-blue-500">
                Submitted {formatDate(myBenefits.pending_change_request.requested_at)}. Speak to HR if it needs changing before it is reviewed.
              </div>
            </div>
          )}

          {/* Current active benefits */}
          {/* ── NO REVISING, ONCE SELECTED (item 28) ──
              "Remove 'Revise' button - not able to revise once selected", and on retest
              "Revise button still there". The word had gone; the button had not — it read
              "Request Changes" and did the same thing, which is the thing that is not
              possible. Selecting is offered while nothing is selected; after that the
              route is a conversation with HR, and the screen says so instead of offering a
              button that cannot deliver. */}
          <div className="flex items-center justify-between">
            <div className="text-[9px] font-bold text-gray-600">Your Active Benefits — {benefitYear}</div>
            {!myBenefits?.pending_change_request && (myBenefits?.points_allocated ?? 0) > 0
              && (myBenefits?.active_benefits?.length ?? 0) === 0 && (
              <button onClick={() => setBenSelectYear(benefitYear)} data-tour="benefits-select"
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80">
                Select Benefits
              </button>
            )}
          </div>
          {(myBenefits?.active_benefits?.length ?? 0) > 0 && !myBenefits?.pending_change_request && (
            <div className="text-[9px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
              Your selection is fixed for {benefitYear} — flexible benefits cannot be changed once they are in place.
              Speak to HR if something needs to change.
            </div>
          )}

          {(myBenefits?.active_benefits?.length ?? 0) === 0 ? (
            <div className="bg-white border border-gray-200 rounded px-4 py-6 text-center text-[10px] text-gray-400" data-tour="benefits-active">
              {myBenefits?.pending_change_request
                ? `Your selection for ${benefitYear} is with HR. It will appear here once it has been approved.`
                : <>No benefits selected for {benefitYear}.{(myBenefits?.points_allocated ?? 0) > 0 && ' Use the button above to select your benefits.'}</>}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded overflow-x-auto" data-tour="benefits-active">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    {['Benefit', 'Description', 'Points', 'Since'].map(h => (
                      <th key={h} className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {myBenefits.active_benefits.map(b => (
                    <tr key={b.id} className="border-b border-gray-100 hover:bg-gold/5">
                      <td className="px-2 py-1.5 text-[10px] font-semibold">{b.name}</td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-500 max-w-[280px]">{b.description || '—'}</td>
                      <td className="px-2 py-1.5 text-center">
                        <span className="text-[10px] font-extrabold text-navy">{b.points_cost}</span>
                        <span className="text-[8px] text-gray-400"> pt{b.points_cost !== 1 ? 's' : ''}</span>
                      </td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-400">{formatDate(b.approved_at || b.selected_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── THE COMING YEAR (February only) ──
              Everyone chooses next year's benefits during February so they are
              approved and in place for 1 March. The server says when the year is
              open (enrolment_years); outside February this block is not shown. */}
          {nextYearBenefits && (
            <div className="bg-white border border-gold/40 rounded p-3 space-y-2" data-tour="benefits-next-year">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[9px] font-bold text-navy">Your Benefits for {nextYearBenefits.year}</div>
                  <div className="text-[8px] text-gray-500">
                    Starts {benefitYearLabel(nextYearBenefits.year).split(' – ')[0]}. Choose now so HR can approve before the year begins.
                    You will have <strong>{nextYearBenefits.points_allocated}</strong> point{nextYearBenefits.points_allocated !== 1 ? 's' : ''}
                    {nextYearBenefits.points_override ? ' (set by HR)' : ''}.
                  </div>
                </div>
                {!nextYearBenefits.locked && !nextYearBenefits.pending_change_request && (nextYearBenefits.points_allocated ?? 0) > 0 && (
                  <button onClick={() => setBenSelectYear(nextYearBenefits.year)}
                    className="px-3 py-1 text-[10px] font-semibold bg-gold text-white rounded hover:bg-gold/80">
                    Select {nextYearBenefits.year} Benefits
                  </button>
                )}
              </div>
              {nextYearBenefits.pending_change_request && (
                <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-[9px] text-blue-700">
                  ✓ Your {nextYearBenefits.year} selection is with HR. Submitted {formatDate(nextYearBenefits.pending_change_request.requested_at)}.
                </div>
              )}
              {nextYearBenefits.locked && (
                <div className="text-[9px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                  Your {nextYearBenefits.year} selection has been approved and is fixed. Speak to HR if something needs to change.
                </div>
              )}
              {(nextYearBenefits.active_benefits?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-1">
                  {nextYearBenefits.active_benefits.map(b => (
                    <span key={b.id} className="text-[8px] bg-navy/10 text-navy px-1.5 py-0.5 rounded font-semibold">
                      {b.name} · {b.points_cost}pt{b.points_cost !== 1 ? 's' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reminders */}
          <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[8px] text-gray-500 space-y-0.5" data-tour="benefits-reminders">
            <div className="font-bold text-gray-600 mb-1">Reminders</div>
            <div>• Flexible benefits are "use it or lose it" — they apply for the benefit year only and do not carry over.</div>
            <div>• Each benefit (except Extra Annual Leave) can only be selected once per year.</div>
            <div>• Benefits cannot be exchanged for cash.</div>
            <div>• Where receipts are required, allowances can only be claimed for eligible costs incurred during the benefit year after the benefit has been selected.</div>
          </div>

          {/* ── STANDARD BENEFITS ──
              Set up on the Standard Benefits tab of Benefits admin. No points
              and no request. The server sends only the ones this person
              qualifies for today, so every card here is theirs; one they will
              reach later appears on the day they do. Hidden while there are
              none, rather than an empty heading. */}
          {standardBenefits.length > 0 && (
            <div className="space-y-2 pt-2" data-tour="benefits-standard">
              <div className="border-b border-gold/40 pb-1">
                <div className="text-[11px] font-extrabold text-navy">Standard Benefits</div>
                <div className="text-[8px] text-gray-500">Benefits you have as an employee. No points, nothing to select.</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {standardBenefits.map(b => (
                  <div key={b.id} className="bg-white border border-gray-200 rounded p-2.5 space-y-1">
                    <div className="text-[10px] font-bold text-navy">{b.name}</div>
                    {b.description && <div className="text-[9px] text-gray-600 whitespace-pre-line">{b.description}</div>}
                    {b.eligibility && (
                      <div className="text-[9px] text-gray-500 whitespace-pre-line"><span className="font-semibold text-gray-600">Who qualifies: </span>{b.eligibility}</div>
                    )}
                    {b.how_to_access && (
                      <div className="text-[9px] text-gray-500 whitespace-pre-line"><span className="font-semibold text-gray-600">How to use it: </span>{b.how_to_access}</div>
                    )}
                    {b.link_url && (
                      <a href={b.link_url} target="_blank" rel="noopener noreferrer" className="inline-block text-[9px] font-semibold text-blue-700 hover:underline">More information ↗</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Overtime / Standby / Call-Out tabs ── */}
      {tab === 'overtime' && (() => {
        // Filter requests by sub-tab type
        const filtered = otSubTab === 'allowances'
          ? otRequests.filter(r => ALLOWANCE_TYPES.includes(r.request_type))
          : otRequests.filter(r => r.request_type === otSubTab);

        // Build sorted month list with data indicators
        const allMonths = [...new Set(filtered.map(r => monthKey(r.start_datetime || r.date)).filter(Boolean))].sort().reverse();
        const currentMonthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
        const activeMonth = otMonth || allMonths[0] || currentMonthKey;

        // In date order. The API lists claims newest-logged first, so a week of
        // standby raised in two sittings came out as 20-24 then 18-19.
        const monthRequests = filtered
          .filter(r => monthKey(r.start_datetime || r.date) === activeMonth)
          .sort((a, b) => otWhen(a).localeCompare(otWhen(b)) || String(a.created_at || '').localeCompare(String(b.created_at || '')));

        const logTypeLabel = otSubTab === 'allowances' ? 'Allowance' : (OT_TYPE_LABELS[otSubTab] || otSubTab);

        return (
          <div>
            {/* Sub-tabs + Log button */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex gap-1 flex-wrap" data-tour="ot-kinds">
                {[['overtime','Overtime'],['standby','Standby'],['call_out','Call-Out'],['allowances','Allowances']].map(([key, label]) => {
                  const count = key === 'allowances'
                    ? otRequests.filter(r => ALLOWANCE_TYPES.includes(r.request_type) && r.status === 'pending').length
                    : otRequests.filter(r => r.request_type === key && r.status === 'pending').length;
                  return (
                    <button key={key} onClick={() => { setOtSubTab(key); setOtMonth(null); }}
                      className={`px-3 py-1 text-[10px] font-semibold rounded relative ${otSubTab === key ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {label}
                      {count > 0 && <span className="ml-1 bg-amber-400 text-amber-950 text-[7px] font-bold px-1 rounded-full">{count}</span>}
                    </button>
                  );
                })}
              </div>
              <button onClick={() => {
                const type = otSubTab === 'allowances' ? allowanceModalType : otSubTab;
                setOtModalType(type);
                setOtForm({ request_type: type, start_datetime: todayDtStr, end_datetime: '', reason: '' });
                setShowOtModal(true);
              }}
                data-tour="ot-log"
                className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">
                + Log {logTypeLabel}
              </button>
            </div>

            {/* Allowances type picker — shown when on Allowances sub-tab */}
            {otSubTab === 'allowances' && (
              <div className="flex gap-2 mb-3 items-center flex-wrap" data-tour="ot-allowance-types">
                <span className="text-[8px] font-bold text-gray-500 uppercase">Log as:</span>
                {ALLOWANCE_TYPES.map(t => (
                  <button key={t} onClick={() => setAllowanceModalType(t)}
                    className={`px-2 py-0.5 text-[9px] font-semibold rounded border transition-all ${
                      allowanceModalType === t ? 'bg-navy text-white border-navy' : 'bg-white border-gray-200 text-gray-600 hover:border-navy/40'
                    }`}>
                    {OT_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            )}

            {/* Suggested from the time log — only on the Overtime sub-tab */}
            {otSubTab === 'overtime' && (() => {
              const open = otSuggestions.filter(isOvertimeSuggestionOpen);
              const weekAgo = Date.now() - 7 * 86400000;
              const setAside = otSuggestions.filter(r => r.status === 'dismissed' && r.dismissed_at
                && new Date(`${r.dismissed_at.replace(' ', 'T')}Z`).getTime() >= weekAgo);
              if (!open.length && !setAside.length) return null;
              const fmtD = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
              const fmtHrs = (h) => (h % 1 === 0 ? `${h}h` : `${Number(h).toFixed(2).replace(/0$/, '')}h`);
              return (
                <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-3" data-tour="ot-suggestions">
                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <div>
                      <div className="text-[10px] font-bold text-orange-800">Suggested from your time log</div>
                      <div className="text-[9px] text-orange-700">Hours you logged on a weekend, a bank holiday or over your daily cap. Claiming opens the request form with the day filled in; add when the overtime ran, then submit. Set aside what was not overtime.</div>
                    </div>
                    {open.length > 0 && <span className="bg-orange-200 text-orange-900 text-[8px] font-bold px-1.5 py-0.5 rounded-full">{open.length} to review</span>}
                  </div>
                  <div className="space-y-1.5">
                    {open.map(r => (
                      <div key={r.key} className="bg-white border border-orange-100 rounded px-2.5 py-2 flex items-center gap-3 flex-wrap">
                        <div className="flex-1 min-w-[180px]">
                          <div className="text-[10px] font-semibold text-navy">
                            {r.kind === 'weekly_excess' ? `Week of ${fmtD(r.date)}` : fmtD(r.date)} · {fmtHrs(r.hours)} · {OT_SUGGESTION_LABELS[r.kind] || r.kind}
                          </div>
                          <div className="text-[9px] text-gray-500">
                            {r.sow_references?.length ? r.sow_references.join(', ') : 'Overhead'}{r.description ? ` · ${r.description}` : ''}
                            {r.eligibility === 'toil' && ' · Your grade earns TOIL for this'}
                            {r.eligibility === 'unknown' && ' · Your grade is not set, so HR will confirm how this is paid'}
                          </div>
                          {otSuggestNote[r.key] && <div className="text-[9px] text-navy mt-1">{otSuggestNote[r.key]}</div>}
                        </div>
                        <div className="flex gap-1.5">
                          <button onClick={() => claimSuggestion(r)} disabled={otSuggestBusy === r.key}
                            className="px-2.5 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50">
                            {r.eligibility === 'toil' ? 'Claim as TOIL…' : 'Claim overtime…'}
                          </button>
                          <button onClick={() => dismissSuggestion(r)} disabled={otSuggestBusy === r.key}
                            className="px-2.5 py-1 bg-white border border-gray-200 text-gray-600 text-[9px] font-semibold rounded hover:bg-gray-50 disabled:opacity-50">
                            Not overtime
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {setAside.length > 0 && (
                    <div className="mt-2 text-[9px] text-gray-500">
                      Set aside: {setAside.map((r, i) => (
                        <span key={r.key}>{i > 0 && ', '}{fmtD(r.date)} {fmtHrs(r.hours)} <button onClick={() => restoreSuggestion(r)} disabled={otSuggestBusy === r.key} className="underline text-navy hover:text-gold">undo</button></span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Summary strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3" data-tour="ot-summary">
              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="text-[8px] text-gray-500 uppercase font-bold">Pending</div>
                <div className={`text-lg font-extrabold ${filtered.filter(r=>r.status==='pending').length ? 'text-amber-500' : 'text-navy'}`}>
                  {filtered.filter(r => r.status === 'pending').length}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="text-[8px] text-gray-500 uppercase font-bold">Total</div>
                <div className="text-lg font-extrabold text-navy">{filtered.length}</div>
              </div>
              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="text-[8px] text-gray-500 uppercase font-bold">Approved Hours</div>
                <div className="text-lg font-extrabold text-navy">
                  {filtered.filter(r=>r.status==='approved').reduce((s,r)=>s+(parseFloat(r.hours)||0),0).toFixed(1)}h
                </div>
              </div>
            </div>

            {/* Month selector, and the switch between the list and the calendar */}
            <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
            {allMonths.length > 0 ? (
              <div className="flex flex-wrap gap-1" data-tour="ot-months">
                {allMonths.map(mk => {
                  const hasData = filtered.some(r => monthKey(r.start_datetime || r.date) === mk);
                  const hasPending = filtered.some(r => monthKey(r.start_datetime || r.date) === mk && r.status === 'pending');
                  return (
                    <button key={mk} onClick={() => setOtMonth(mk)}
                      className={`flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold rounded border transition-all ${
                        activeMonth === mk ? 'bg-navy text-white border-navy' : 'bg-white border-gray-200 text-gray-600 hover:border-navy/40'
                      }`}>
                      {hasData && <span className={`w-1.5 h-1.5 rounded-full ${hasPending ? 'bg-amber-400' : 'bg-emerald-400'} ${activeMonth === mk ? 'bg-white' : ''}`} />}
                      {monthLabel(mk)}
                    </button>
                  );
                })}
              </div>
            ) : <div />}
              <div className="flex gap-1" data-tour="ot-view" role="group" aria-label="Show claims as">
                {[['list', 'List'], ['calendar', 'Calendar']].map(([key, label]) => (
                  <button key={key} type="button" aria-pressed={otView === key} onClick={() => setOtView(key)}
                    className={`px-2 py-0.5 text-[9px] font-semibold rounded border transition-all ${
                      otView === key ? 'bg-navy text-white border-navy' : 'bg-white border-gray-200 text-gray-600 hover:border-navy/40'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {otView === 'calendar' ? (
              <OtMonthCalendar month={activeMonth} requests={monthRequests} bankHolidays={bankHolidays}
                showType={otSubTab === 'allowances'} onView={setSelectedOt} />
            ) : (
            /* Requests table for selected month */
            <div className="bg-white border border-gray-200 rounded overflow-x-auto" data-tour="ot-requests">
              <table className="w-full">
                <thead>
                  <tr>
                    {otSubTab === 'allowances' && <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Type</th>}
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Start</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">End</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Hours</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Status</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Description</th>
                    <th className="px-2 py-1 border-b bg-gray-50"></th>
                  </tr>
                </thead>
                <tbody>
                  {monthRequests.map(r => (
                    <tr key={r.id} className="hover:bg-gold/5">
                      {otSubTab === 'allowances' && <td className="px-2 py-1 text-[9px] font-medium whitespace-nowrap">{OT_TYPE_LABELS[r.request_type] || r.request_type}</td>}
                      <td className="px-2 py-1 text-[9px]">{formatDatetime(r.start_datetime || r.date)}</td>
                      <td className="px-2 py-1 text-[9px]">{r.end_datetime ? formatDatetime(r.end_datetime) : '—'}</td>
                      <td className="px-2 py-1 text-[10px] font-semibold text-right">{r.hours ? `${r.hours}h` : '—'}</td>
                      <td className="px-2 py-1">
                        <span className={`inline-block px-1.5 py-0.5 text-[8px] font-bold uppercase rounded border ${
                          r.status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                          r.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-300' :
                          r.status === 'cancelled' ? 'bg-gray-100 text-gray-400 border-gray-200' :
                          'bg-amber-100 text-amber-700 border-amber-300'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-[9px] text-gray-400 max-w-[120px] truncate">{r.reason || '—'}</td>
                      <td className="px-2 py-1 whitespace-nowrap">
                        <button onClick={() => setSelectedOt(r)} className="text-[9px] text-navy underline hover:text-gold mr-2">View</button>
                        {r.status === 'pending' && (
                          <button onClick={() => cancelOtRequest(r.id)} className="text-[9px] text-red-400 underline hover:text-red-600">Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {monthRequests.length === 0 && (
                    <tr><td colSpan={otSubTab === 'allowances' ? 7 : 6} className="text-center py-6 text-[10px] text-gray-400">
                      {allMonths.length === 0 ? `No ${logTypeLabel.toLowerCase()} requests submitted yet` : `No entries for ${monthLabel(activeMonth)}`}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            )}
          </div>
        );
      })()}

      {/* Log Time / Allowance Modal */}
      {showOtModal && (() => {
        const isAllowanceType = ALLOWANCE_TYPES.includes(otForm.request_type);
        const typeLabel = OT_TYPE_LABELS[otForm.request_type] || otForm.request_type;
        const computedHours = calcHours(otForm.start_datetime, otForm.end_datetime);
        const descPlaceholder =
          otForm.request_type === 'call_out' ? 'Describe the call-out reason…' :
          otForm.request_type === 'desk_move_weekday' || otForm.request_type === 'desk_move_weekend' ? 'Describe the desk move (e.g. site, reason)…' :
          otForm.request_type === 'remote_working' ? 'Describe the remote working location / reason…' :
          otForm.request_type === 'out_of_hours' ? 'Describe the out-of-hours work carried out…' :
          'Describe the work carried out…';
        const sug = otForm.suggestion_key ? otForm.suggestion : null;
        const sugDay = sug ? new Date(`${sug.date}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';
        return (
          <Modal title={`${sug ? 'Claim' : 'Log'} ${typeLabel}`} onClose={() => setShowOtModal(false)}
            dataTour="ot-modal" closeDataTour="ot-modal-close"
            footer={
              <>
                <button onClick={() => setShowOtModal(false)} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
                <button onClick={submitOtRequest} disabled={!otForm.start_datetime || !otForm.end_datetime || computedHours <= 0}
                  className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">Submit</button>
              </>
            }>
            <div className="grid grid-cols-2 gap-2" data-tour="ot-form">
              {/* For allowance types, allow switching type inside the modal */}
              {isAllowanceType ? (
                <div className="col-span-2">
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Allowance Type</label>
                  <div className="flex flex-wrap gap-1">
                    {ALLOWANCE_TYPES.map(t => (
                      <button key={t} onClick={() => setOt('request_type', t)}
                        className={`px-2 py-0.5 text-[9px] font-semibold rounded border transition-all ${
                          otForm.request_type === t ? 'bg-navy text-white border-navy' : 'bg-white border-gray-200 text-gray-600 hover:border-navy/40'
                        }`}>
                        {OT_TYPE_LABELS[t]}
                      </button>
                    ))}
                  </div>
                  <div className="text-[8px] text-gray-400 mt-1">Your line manager approves the claim; the Overtime Policy decides what it pays.</div>
                </div>
              ) : (
                <div className="col-span-2">
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Type</label>
                  <div className="text-[11px] font-semibold text-navy">{typeLabel}</div>
                  <div className="text-[8px] text-gray-400 mt-0.5">Your line manager approves the claim; the Overtime Policy decides what it pays.</div>
                </div>
              )}
              {sug && (
                <div className="col-span-2 bg-orange-50 border border-orange-200 rounded px-2 py-1.5 text-[9px] text-orange-800">
                  <strong>From your time log:</strong> {sug.kind === 'weekly_excess' ? `week of ${sugDay}` : sugDay} · {sug.hours}h · {OT_SUGGESTION_LABELS[sug.kind] || sug.kind}
                  {sug.sow_references.length ? ` · ${sug.sow_references.join(', ')}` : ''}.
                  {' '}The log has no clock times, so give the start and end; the hours claimed follow those times.
                </div>
              )}
              {sug ? (
                <>
                  {/* From a suggestion: the date is known and pre-filled, the
                      times are not, so they start empty rather than guessed. */}
                  <div className="col-span-2">
                    <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Date *</label>
                    <input className="form-input" type="date" value={otForm.date || ''}
                      onChange={e => setOt('date', e.target.value)} max={today} />
                  </div>
                  <div>
                    <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Start Time *</label>
                    <input className="form-input" type="time" value={otForm.start_time || ''}
                      onChange={e => setOt('start_time', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">End Time *</label>
                    <input className="form-input" type="time" value={otForm.end_time || ''}
                      onChange={e => setOt('end_time', e.target.value)} />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Start Date & Time *</label>
                    <input className="form-input" type="datetime-local" value={otForm.start_datetime}
                      onChange={e => setOt('start_datetime', e.target.value)} max={today + 'T23:59'} />
                  </div>
                  <div>
                    <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">End Date & Time *</label>
                    <input className="form-input" type="datetime-local" value={otForm.end_datetime}
                      onChange={e => setOt('end_datetime', e.target.value)} max={today + 'T23:59'} />
                  </div>
                </>
              )}
              {computedHours > 0 && (
                <div className="col-span-2">
                  <div className="bg-blue-50 border border-blue-100 rounded px-2 py-1.5 text-[9px] text-blue-700">
                    Duration: <strong>{computedHours.toFixed(2)} hours</strong>
                  </div>
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description *</label>
                <textarea className="form-textarea" rows="2" value={otForm.reason}
                  onChange={e => setOt('reason', e.target.value)}
                  placeholder={descPlaceholder} />
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* View existing overtime / standby / call-out request */}
      {selectedOt && (
        <Modal title={`${OT_TYPE_LABELS[selectedOt.request_type] || selectedOt.request_type} — ${formatDate(selectedOt.date)}`}
          onClose={() => setSelectedOt(null)}
          footer={<button onClick={() => setSelectedOt(null)} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Close</button>}>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div><span className="text-[8px] text-gray-500 uppercase font-bold block">Type</span>{OT_TYPE_LABELS[selectedOt.request_type] || selectedOt.request_type}</div>
            <div><span className="text-[8px] text-gray-500 uppercase font-bold block">Duration</span>{selectedOt.hours ? `${selectedOt.hours}h` : '—'}</div>
            <div><span className="text-[8px] text-gray-500 uppercase font-bold block">Start</span>{formatDatetime(selectedOt.start_datetime || selectedOt.date)}</div>
            <div><span className="text-[8px] text-gray-500 uppercase font-bold block">End</span>{selectedOt.end_datetime ? formatDatetime(selectedOt.end_datetime) : '—'}</div>
            <div className="col-span-2 flex items-center gap-2">
              <span className="text-[8px] text-gray-500 uppercase font-bold">Status:</span>
              <span className={`inline-block px-1.5 py-0.5 text-[8px] font-bold uppercase rounded border ${
                selectedOt.status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                selectedOt.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-300' :
                'bg-amber-100 text-amber-700 border-amber-300'}`}>{selectedOt.status}</span>
              {selectedOt.approver_name && <span className="text-[8px] text-gray-400">by {selectedOt.approver_name}</span>}
            </div>
            {selectedOt.reason && <div className="col-span-2"><span className="text-[8px] text-gray-500 uppercase font-bold block">Description</span>{selectedOt.reason}</div>}
            {selectedOt.status === 'rejected' && selectedOt.rejection_reason && (
              <div className="col-span-2 bg-red-50 border border-red-200 rounded p-2">
                <div className="text-[8px] font-bold text-red-600 uppercase mb-0.5">Rejection Reason</div>
                <div className="text-[10px] text-red-700">{selectedOt.rejection_reason}</div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Benefit Selection Modal */}
      {benSelectYear && (() => {
        const forYear = benSelectYear === nextYearBenefits?.year ? nextYearBenefits : myBenefits;
        return (
          <BenefitSelectionModal
            year={benSelectYear}
            allBenefits={allBenefits}
            currentSelections={forYear?.active_benefits?.map(b => b.benefit_id) || []}
            pointsAllocated={forYear?.points_allocated || 0}
            onClose={() => setBenSelectYear(null)}
            onSubmit={submitBenefitRequest}
          />
        );
      })()}

      {/* Request Leave Modal */}
      {showRequest && (
        <Modal
          title={changing ? 'Change approved leave' : 'Request Leave'}
          dataTour="leave-request-modal"
          closeDataTour="leave-request-modal-close"
          onClose={closeRequest}
          footer={
            <>
              <button onClick={closeRequest} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">{changing ? 'Keep it as it is' : 'Cancel'}</button>
              <button
                onClick={submitLeaveRequest}
                disabled={submitting || !reqForm.start_date || !reqForm.end_date || !reqForm.days}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
              >
                {submitting ? 'Submitting…' : changing ? 'Send change for approval' : 'Submit Request'}
              </button>
            </>
          }
        >
          {changing && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-[9px] text-amber-700">
              Currently {changing.days}d from {formatDate(changing.start_date)} to {formatDate(changing.end_date)}.
              The change goes to your manager, and this leave stays booked as it is until they decide.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2" data-tour="leave-request-type">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Leave Type</label>
              <select className="form-select" value={reqForm.leave_type}
                onChange={e => {
                  const v = e.target.value;
                  setReqForm(p => ({
                    ...p, leave_type: v,
                    // Item 32: a wellbeing day is one standalone day, so the form
                    // stops offering a range for it rather than letting somebody
                    // pick a week and be refused on submit.
                    ...(v === 'wellbeing' ? { end_date: p.start_date, days: p.start_date ? '1' : '' } : {}),
                  }));
                }}>
                {LEAVE_TYPES.filter(t => t.value !== 'toil' || toilAvailable).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {/* What this particular allowance is and how it behaves, at the
                  point of choosing it. */}
              {SEPARATE_BOX_TYPES[reqForm.leave_type]?.note && (
                <div className="text-[7px] text-gray-500 mt-0.5">
                  {separateBoxes.find(b => b.type === reqForm.leave_type)?.note
                    || SEPARATE_BOX_TYPES[reqForm.leave_type].note}
                </div>
              )}
            </div>
            <div data-tour="leave-request-dates">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">
                {reqForm.leave_type === 'wellbeing' ? 'Date *' : 'Start Date *'}
              </label>
              <input className="form-input" type="date" value={reqForm.start_date}
                onChange={e => {
                  const v = e.target.value;
                  setReqForm(p => ({
                    ...p, start_date: v,
                    ...(p.leave_type === 'wellbeing' ? { end_date: v, days: '1' } : {}),
                  }));
                }} />
            </div>
            {reqForm.leave_type !== 'wellbeing' && (
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">End Date *</label>
                <input className="form-input" type="date" value={reqForm.end_date} onChange={e => setR('end_date', e.target.value)} />
              </div>
            )}
            {/* ── HALF DAYS ──
                One day offers all day, morning only or afternoon only. A range
                can start at lunchtime or end at it, so "off from Wednesday
                lunchtime" is sayable rather than rounded up to a whole day. */}
            {reqForm.start_date && reqForm.start_date === (reqForm.end_date || reqForm.start_date) ? (
              <div className="col-span-2">
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Which part of the day</label>
                <select className="form-select"
                  value={reqForm.end_half === 'am' ? 'am' : reqForm.start_half === 'pm' ? 'pm' : 'full'}
                  onChange={e => {
                    const v = e.target.value;
                    setReqForm(p => ({ ...p, start_half: v === 'pm' ? 'pm' : 'full', end_half: v === 'am' ? 'am' : 'full' }));
                  }}>
                  <option value="full">All day</option>
                  <option value="am">Morning only (half day)</option>
                  <option value="pm">Afternoon only (half day)</option>
                </select>
              </div>
            ) : reqForm.start_date && reqForm.end_date ? (
              <>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">First day</label>
                  <select className="form-select" value={reqForm.start_half} onChange={e => setR('start_half', e.target.value)}>
                    <option value="full">Full day</option>
                    <option value="pm">Afternoon only (half day)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Last day</label>
                  <select className="form-select" value={reqForm.end_half} onChange={e => setR('end_half', e.target.value)}>
                    <option value="full">Full day</option>
                    <option value="am">Morning only (half day)</option>
                  </select>
                </div>
              </>
            ) : null}
            <div data-tour="leave-request-days">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Number of Days</label>
              <input className="form-input bg-gray-50 text-gray-500 cursor-default" type="number" step="0.5" min="0.5"
                value={reqForm.days} readOnly tabIndex={-1} aria-readonly="true" />
              {reqForm.start_date && reqForm.end_date && !reqForm.days ? (
                <div className="text-[7px] text-amber-600 mt-0.5">No working days in those dates, so there is nothing to book.</div>
              ) : (
                <div className="text-[7px] text-gray-400 mt-0.5">Calculated from the dates and the half-day choices</div>
              )}
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Reason (optional)</label>
              <input className="form-input" value={reqForm.reason} onChange={e => setR('reason', e.target.value)} placeholder="Optional note for approver" />
            </div>
          </div>
          {annualAllowance && (
            <div className="mt-3 p-2 bg-blue-50 border border-blue-100 rounded text-[9px] text-blue-700">
              Annual leave: <strong>{annualAllowance.total_days - (annualAllowance.booked_days || 0)}d</strong> remaining of {annualAllowance.total_days}d allowance
            </div>
          )}
          {submitError && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-[9px] text-red-700">{submitError}</div>
          )}
        </Modal>
      )}

      {/* The benefits tour is gated on the same flag the scheme renders by, so
          somebody it does not cover is never shown a tour of it. */}
      {tour.open && (
        <FeatureTour steps={TOURS[tab].steps} context={{ inBenefitsScheme, hasStandardBenefits: standardBenefits.length > 0 }} onFinish={tour.finish} onSkip={tour.skip} />
      )}
    </div>
  );
}

// ── Benefit Selection Modal ────────────────────────────────────────────────────
// ── A month of claims as a calendar ──────────────────────────────────────────
// The list says which date a claim fell on; this says which day of the week,
// which is how standby is worked out (weekday evening or weekend day). The
// same Monday-first grid as the PLOW standby calendar, read-only: a chip is a
// claim, on the day it starts, and opens the same View as the list.
const OT_CHIP_TONES = {
  approved:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected:  'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-400 border-gray-200 line-through',
  pending:   'bg-amber-50 text-amber-700 border-amber-200',
};

function OtMonthCalendar({ month, requests, bankHolidays, showType, onView }) {
  const grid = monthGrid(month);
  const todayISO = new Date().toISOString().slice(0, 10);
  const byDay = new Map();
  for (const r of requests) {
    const day = otWhen(r).slice(0, 10);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(r);
  }
  return (
    <div data-tour="ot-requests">
      <div className="grid grid-cols-7 gap-px text-center text-[8px] font-bold text-gray-500 uppercase mb-1">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded overflow-hidden">
        {grid.weeks.flat().map(iso => {
          const inMonth = grid.inMonth(iso);
          const dow = new Date(`${iso}T00:00:00Z`).getUTCDay();
          const holiday = bankHolidays?.has?.(iso);
          const day = inMonth ? (byDay.get(iso) || []) : [];
          return (
            <div key={iso}
              className={`min-h-[64px] min-w-0 p-1 flex flex-col gap-0.5
                ${inMonth ? (dow === 0 || dow === 6 || holiday ? 'bg-gray-50' : 'bg-white') : 'bg-gray-100'}
                ${iso === todayISO ? 'outline outline-1 outline-gold -outline-offset-1' : ''}`}>
              <div className="flex items-baseline justify-between gap-1">
                <span className={`text-[10px] font-semibold text-gray-700 ${inMonth ? '' : 'opacity-30'}`}>{Number(iso.slice(8))}</span>
                {inMonth && holiday && <span className="text-[8px] text-gold font-semibold truncate">Bank hol</span>}
              </div>
              {day.map(r => (
                <button key={r.id} type="button" onClick={() => onView(r)}
                  title={`${formatDatetime(r.start_datetime || r.date)} · ${r.status}${r.reason ? ` · ${r.reason}` : ''}`}
                  className={`block w-full truncate text-left rounded border px-1 text-[9px] leading-4 font-semibold hover:opacity-80 ${OT_CHIP_TONES[r.status] || OT_CHIP_TONES.pending}`}>
                  {showType ? `${OT_TYPE_LABELS[r.request_type] || r.request_type}${r.hours ? ` · ${r.hours}h` : ''}` : `${r.hours ? `${r.hours}h` : '—'} · ${r.status}`}
                </button>
              ))}
            </div>
          );
        })}
      </div>
      <p className="mt-1 text-[9px] text-gray-400">
        Each claim sits on the day it starts. Amber is pending, green approved, red rejected; cancelled is struck through. Select one to view it; a pending claim is cancelled from the list.
      </p>
    </div>
  );
}

function BenefitSelectionModal({ year, allBenefits, currentSelections, pointsAllocated, onClose, onSubmit }) {
  // Build initial selection state: array of benefit_ids (duplicates for multi-select)
  const [selected, setSelected] = useState([...currentSelections]);

  const pointsUsed = selected.reduce((s, id) => {
    const b = allBenefits.find(x => x.id === id);
    return s + (b?.points_cost || 0);
  }, 0);
  const remaining = pointsAllocated - pointsUsed;

  const countOf = (id) => selected.filter(x => x === id).length;

  const addSelection = (id) => {
    const b = allBenefits.find(x => x.id === id);
    if (!b) return;
    if (countOf(id) >= b.max_selections) return;      // max reached
    if (remaining < b.points_cost) return;             // not enough points
    setSelected(prev => [...prev, id]);
  };

  const removeSelection = (id) => {
    const idx = [...selected].reverse().indexOf(id);
    if (idx === -1) return;
    const fwd = selected.length - 1 - idx;
    setSelected(prev => prev.filter((_, i) => i !== fwd));
  };

  return (
    <Modal
      title={`Select Your Benefits${year ? ` for ${year}` : ''}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button
            onClick={() => onSubmit(selected)}
            className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded"
          >
            Submit Request
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {/* Points meter */}
        <div className="flex items-center gap-4 bg-gray-50 border border-gray-200 rounded p-2">
          <div>
            <div className="text-[8px] text-gray-500 uppercase font-bold">Points Allocated</div>
            <div className="text-lg font-extrabold text-navy">{pointsAllocated}</div>
          </div>
          <div>
            <div className="text-[8px] text-gray-500 uppercase font-bold">Used</div>
            <div className="text-lg font-extrabold text-amber-500">{pointsUsed}</div>
          </div>
          <div>
            <div className={`text-[8px] uppercase font-bold ${remaining < 0 ? 'text-red-500' : 'text-gray-500'}`}>Remaining</div>
            <div className={`text-lg font-extrabold ${remaining < 0 ? 'text-red-500' : 'text-emerald-500'}`}>{remaining}</div>
          </div>
          <div className="flex-1">
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all ${remaining < 0 ? 'bg-red-500' : 'bg-gold'}`}
                style={{ width: `${Math.min(100, (pointsUsed / (pointsAllocated || 1)) * 100)}%` }}
              />
            </div>
          </div>
        </div>

        <div className="text-[8px] text-gray-500 bg-blue-50 border border-blue-100 rounded px-2 py-1">
          Select benefits using the <strong>+</strong> and <strong>−</strong> buttons. Your request will be reviewed by HR before it takes effect.
        </div>

        {/* Benefit options */}
        <div className="space-y-1.5">
          {allBenefits.map(b => {
            const count = countOf(b.id);
            const canAdd = count < b.max_selections && remaining >= b.points_cost;
            const totalCost = b.points_cost * count;
            return (
              <div key={b.id} className={`bg-white border rounded p-2 flex items-start gap-2 ${count > 0 ? 'border-gold/40 bg-gold/5' : 'border-gray-200'}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold">{b.name}</span>
                    <span className="text-[8px] font-bold text-navy">{b.points_cost}pt{b.points_cost !== 1 ? 's' : ''}</span>
                    {b.max_selections > 1 && (
                      <span className="text-[7px] text-gray-400">(max {b.max_selections}×)</span>
                    )}
                  </div>
                  {b.description && (
                    <div className="text-[8px] text-gray-400 mt-0.5 leading-relaxed">{b.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                  {count > 0 && (
                    <button onClick={() => removeSelection(b.id)}
                      className="w-5 h-5 rounded-full bg-red-100 text-red-600 font-bold text-[10px] flex items-center justify-center hover:bg-red-200">−</button>
                  )}
                  {count > 0 && (
                    <span className="text-[10px] font-extrabold text-gold w-4 text-center">{count}</span>
                  )}
                  <button onClick={() => addSelection(b.id)} disabled={!canAdd}
                    className="w-5 h-5 rounded-full bg-gold/20 text-gold font-bold text-[10px] flex items-center justify-center hover:bg-gold/40 disabled:opacity-30 disabled:cursor-not-allowed">+</button>
                  {totalCost > 0 && (
                    <span className="text-[8px] text-gray-400 ml-1">{totalCost}pt used</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {selected.length === 0 && (
          <div className="text-[9px] text-gray-400 italic text-center">No benefits selected — submitting will remove all current benefits.</div>
        )}
      </div>
    </Modal>
  );
}

// ── The four pages ───────────────────────────────────────────────────────────
//
// Each one is an address of its own in My Space → My Info. They render the same
// component with the tab pinned, because the record behind them is one record.
export function MyContactPage()    { return <MyInfoPage tab="contact" />; }
export function MyLeavePage()      { return <MyInfoPage tab="leave" />; }
export function MyBenefitsPage()   { return <MyInfoPage tab="benefits" />; }
export function MyAllowancesPage() { return <MyInfoPage tab="overtime" />; }
